import {
  Dispatch,
  SetStateAction,
  useDebugValue,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePersistFn } from '../../hooks/usePersistFn';

export const deferredValidSymbol = Symbol('useForm deferred validate fn');

export interface ValidateResult$Valid {
  type: 'valid';
}

export interface ValidateResult$Invalid {
  type: 'invalid';
  msg: string;
  field?: string;
}

export type ValidateResult = ValidateResult$Valid | ValidateResult$Invalid;

type ValidateFn<T, All> = (input: T, all: All) => ValidateResult;

export type FormValidateOption<FormItemData, All> = ValidateFn<
  FormItemData,
  All
>;
export type FormDeferredValidateOption<FormItemData, FormData> = {
  [deferredValidSymbol]: (
    formData: FormData,
  ) => ValidateFn<FormItemData, FormData>;
};
export type FormValidOption<FormItemData, FormData> =
  | FormValidateOption<FormItemData, FormData>
  | FormDeferredValidateOption<FormItemData, FormData>;

type FormOption<FormItemData, FormData> = {
  defaultValue: FormItemData;
  valid?: FormValidOption<FormItemData, FormData>;
};

type FormLoadingState<FormData> = { [K in keyof FormData]?: boolean };

type CustomErrorState<FormData> = { [K in keyof FormData]?: string | null };

export type FormController<FormData> = {
  value: FormData;
  initialValues: FormData;
  loadingState: FormLoadingState<FormData>;
  customErrors: CustomErrorState<FormData>;
  valid: Record<keyof FormData, ValidateResult>;
  allValid: boolean;
  update: Required<{ [K in keyof FormData]: (newValue: FormData[K]) => void }>;
  error: Required<{
    [K in keyof FormData]: (errorMsg: string | null | undefined) => void;
  }>;
  loading: Required<{ [K in keyof FormData]: (loading: boolean) => void }>;
  updateAll: Dispatch<SetStateAction<FormData>>;
  validated: boolean;
  validate: () => boolean;
};

export type FormOptions<FormData> = {
  [K in keyof FormData]: FormOption<FormData[K], FormData>;
};

export type FormOptionsWithOptionalDefaultValues<FormData> = {
  [K in keyof FormData]: Partial<FormOption<FormData[K], FormData>>;
};

export const useValidateFormData = <FormData extends { [key: string]: any }>(
  options: FormOptionsWithOptionalDefaultValues<FormData>,
) => {
  type FormValidResults = FormController<FormData>['valid'];
  return usePersistFn((data: FormData) => {
    const keys = Object.keys(options) as (keyof FormData)[];
    const validResults = keys.reduce((acc, key) => {
      acc[key] = getValidateFn(options[key].valid, data)(data[key], data);
      return acc;
    }, {} as FormValidResults);
    const isAllValid = Object.values(validResults).every(
      r => r.type === 'valid',
    );
    return [isAllValid, validResults] as const;
  });
};

export function useForm<FormData extends { [key: string]: any }>(
  options: FormOptions<FormData>,
  params?: {
    resetCustomErrorOnChange?: boolean;
  },
): FormController<FormData> {
  type FormValidResults = FormController<FormData>['valid'];
  type FormUpdateFns = FormController<FormData>['update'];
  type FormErrorUpdateFns = FormController<FormData>['error'];
  type FormLoadingStateUpdateFns = FormController<FormData>['loading'];
  type CustomErrors = { [K in keyof FormData]?: string | null };

  const resetCustomErrorOnChange = params?.resetCustomErrorOnChange;

  const [keys] = useState(() => Object.keys(options) as (keyof FormData)[]);

  const [data, updateData] = useState(getInitialData);

  const initialValues = useRef<FormData>(data).current;

  const [loadingState, setLoadingState] = useState<FormLoadingState<FormData>>(
    {},
  );

  const [customErrors, setCustomErrors] = useState<CustomErrors>({});

  const lastValidResults = useRef<FormValidResults>();
  const validResults = getValidResults();
  lastValidResults.current = validResults;

  const updateFns = useMemo(() => {
    const result = {} as FormUpdateFns;
    for (const key of keys) {
      result[key] = newValue => {
        updateData(data => ({
          ...data,
          [key]: newValue,
        }));
        // clear custom errors when value is changed.
        if (resetCustomErrorOnChange) {
          setCustomErrors(data => ({
            ...data,
            [key]: undefined,
          }));
        }
      };
    }
    return result;
  }, [keys, updateData, resetCustomErrorOnChange]);

  const errorFns = useMemo(() => {
    const result = {} as FormErrorUpdateFns;
    for (const key of keys) {
      result[key] = errorMsg => {
        setCustomErrors(data => ({
          ...data,
          [key]: errorMsg,
        }));
      };
    }
    return result;
  }, [keys, setCustomErrors]);

  const loadingFns = useMemo(() => {
    const result = {} as FormLoadingStateUpdateFns;
    for (const key of keys) {
      result[key] = loading => {
        setLoadingState(data => ({
          ...data,
          [key]: loading,
        }));
      };
    }
    return result;
  }, [keys, setLoadingState]);

  const checkValidity = () =>
    Object.values(loadingState).every(x => !x) &&
    Object.values(validResults).every(r => r.type === 'valid');

  const allValid = checkValidity();

  const [forceValidate, setForceValidate] = useState(0);

  const validate = usePersistFn(() => {
    setForceValidate(v => v + 1);
    return checkValidity();
  });

  const res: FormController<FormData> = useMemo(
    () => ({
      value: data,
      valid: validResults,
      update: updateFns,
      allValid,
      updateAll: updateData,
      error: errorFns,
      loadingState,
      customErrors,
      loading: loadingFns,
      validate,
      validated: forceValidate > 0,
      initialValues,
    }),
    [
      data,
      validResults,
      updateFns,
      allValid,
      errorFns,
      loadingState,
      customErrors,
      loadingFns,
      validate,
      forceValidate,
      initialValues,
    ],
  );

  useDebugValue(res);

  return res;

  function getValidResults(): FormValidResults {
    let hasChanges = false;
    const results = keys.reduce((acc, key) => {
      let res = getValidateFn(options[key].valid, data)(data[key], data);
      if (customErrors[key]) {
        res = { type: 'invalid', msg: customErrors[key]! };
      }
      if (!validateResultEquals(res, lastValidResults.current?.[key])) {
        hasChanges = true;
      }
      acc[key] = res;
      return acc;
    }, {} as FormValidResults);
    if (!hasChanges && lastValidResults.current) {
      // if no changes, return the last valid results to
      // avoid unnecessary re-renders
      return lastValidResults.current;
    }
    return results;
  }

  function getInitialData(): FormData {
    return keys.reduce((acc, key) => {
      acc[key] = options[key].defaultValue;
      return acc;
    }, {} as FormData);
  }
}

function validateResultEquals(
  a: ValidateResult | undefined,
  b: ValidateResult | undefined,
): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  if (a.type !== b.type) return false;
  if (a.type === 'invalid' && b.type === 'invalid') {
    return a.msg === b.msg && (a.field ?? '') === (b.field ?? '');
  }
  return true;
}

function getValidateFn<T, ALL>(
  valid: undefined | FormValidOption<T, ALL>,
  all: ALL,
): ValidateFn<T, ALL> {
  if (!valid) return required;
  if (deferredValidSymbol in valid) {
    const _valid = valid as FormDeferredValidateOption<T, ALL>;
    return _valid[deferredValidSymbol](all);
  }
  return valid as FormValidateOption<T, ALL>;
}

export function deferredValid<FormItemData, FormData>(
  fn: (formData: FormData) => ValidateFn<FormItemData, FormData>,
): FormDeferredValidateOption<FormItemData, FormData> {
  return { [deferredValidSymbol]: fn };
}

export const validationUndefined = (input: any): ValidateResult => {
  if (input != null) {
    return { type: 'invalid', msg: 'undefined' };
  }
  return { type: 'valid' };
};

export function required(input: any): ValidateResult {
  if (typeof input === 'string' ? Boolean(input.trim()) : input != null) {
    return { type: 'valid' };
  }
  return {
    type: 'invalid',
    msg: 'This field is required',
  };
}

export const requiredIf = <T>(predict: (data: T) => boolean) => {
  return (input: any, data: any): ValidateResult => {
    if (predict(data)) {
      if (typeof input === 'string' ? Boolean(input.trim()) : input != null) {
        return { type: 'valid' };
      }
      return {
        type: 'invalid',
        msg: 'This field is required',
      };
    }
    return { type: 'valid' };
  };
};

export const validationOr =
  <T, U>(validators: ((input: T, data: U) => ValidateResult)[]) =>
  (input: T, data: U): ValidateResult => {
    if (validators.length === 0) {
      return { type: 'valid' };
    }
    let errorResult: ValidateResult = { type: 'valid' };
    for (const validator of validators) {
      const result = validator(input, data);
      if (result.type === 'valid') {
        return result;
      }
      if (result.type === 'invalid') {
        errorResult = result;
      }
    }
    return errorResult;
  };

export const validationAnd =
  <T, U>(validators: ((input: T, data: U) => ValidateResult)[]) =>
  (input: T, data: U): ValidateResult => {
    for (const validator of validators) {
      const result = validator(input, data);
      if (result.type === 'invalid') {
        return result;
      }
    }
    return { type: 'valid' };
  };

export function optional(_input: any): ValidateResult {
  return { type: 'valid' };
}

export function allPass<T, ALL>(
  ...validOpts: FormValidOption<T, ALL>[]
): FormDeferredValidateOption<T, ALL> {
  return deferredValid(
    all => input =>
      validOpts
        .map(o => getValidateFn(o, all)(input, all))
        .find(f => f.type === 'invalid') ?? { type: 'valid' },
  );
}

export function anyPass<T, ALL>(
  ...validOpts: FormValidOption<T, ALL>[]
): FormDeferredValidateOption<T, ALL> {
  return deferredValid(
    all => input =>
      validOpts
        .map(o => getValidateFn(o, all)(input, all))
        .find(f => f.type === 'invalid') ?? { type: 'valid' },
  );
}

export const renderControlFormField =
  <T extends { [key: string]: any }>(
    ctrl: FormController<T>,
    interacted?: boolean,
  ) =>
  <K extends keyof T>(field: K) => {
    const valid: ValidateResult = ctrl.valid[field];
    return {
      value: ctrl.value[field],
      onChangeText: ctrl.update[field],
      loading: ctrl.loadingState[field],
      interacted: interacted || ctrl.validated,
      errorMsg: valid.type === 'invalid' ? valid.msg : undefined,
      fieldName: field,
    };
  };

export const demand = <T, V extends keyof T>(options?: {
  msg?: string;
  when?: (data: T) => boolean;
  predict?: (value: T[V], data: T) => boolean;
}) => {
  return (input: any, data: any): ValidateResult => {
    if (options?.when != null && !options.when(data)) {
      return { type: 'valid' };
    }
    const predict =
      options?.predict ??
      ((value: any, _data: T) => {
        return typeof value === 'string'
          ? Boolean(value.trim())
          : value != null;
      });
    if (predict(input, data)) {
      return { type: 'valid' };
    }
    return {
      type: 'invalid',
      msg: options?.msg ?? 'This field is required',
    };
  };
};
